import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, linkOptions, useParams, useNavigate } from "@tanstack/react-router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";
import { ArrowLeft } from "lucide-react";
import { encountersQueryOptions } from "../../encounters/api/queries";
import { FormattedEncounter } from "../../encounters/types";
import { EncounterCard } from "../../encounters/components/encounter-card";
import { toTitleCase } from "@workspace/utils/string/index"
import { useState } from "react";
import { TodoDetails } from "../components/todos/todo-details";
import { getApiProgram } from "~/lib/utils";

export const todosLinkOptions = (program = 'weight-loss', tab = 'consultation') => linkOptions({
    to: '/dashboard/$program/todos/$tab',
    params: { program, tab }
})

export function TodosPage() {
    const { tab, program } = useParams({ strict: false })
    const navigate = useNavigate()
    const [isEncounterOpen, setEncounterOpen] = useState(false);
    const [selectedEncounter, setSelectedEncounter] = useState<FormattedEncounter | null>(null);
    const apiProgram = getApiProgram(program ?? "")

    const todosEncountersQuery = useSuspenseQuery(encountersQueryOptions({ limit: 10, skip: 0, type: tab, date_filter: "today", status: "unresolved", program: apiProgram }));

    const consultationsTodosEncountersQuery = useSuspenseQuery(encountersQueryOptions({ limit: 10, skip: 0, type: "consultation", date_filter: "today", type_sort_order: "consultation", status: "unresolved", program: apiProgram }));

    const appointmentsTodosEncountersQuery = useSuspenseQuery(encountersQueryOptions({ limit: 10, skip: 0, type: "appointment", date_filter: "today", type_sort_order: "appointment", status: "unresolved", program: apiProgram }));

    const refillsTodosEncountersQuery = useSuspenseQuery(encountersQueryOptions({ limit: 10, skip: 0, type: "refill", date_filter: "today", type_sort_order: "refill", status: "unresolved", program: apiProgram }));

    const handleTabChange = (tab: string) => {
        navigate(todosLinkOptions(program, tab))
        setSelectedEncounter(null);
    };

    return (
        <section
            className="flex flex-col w-screen min-h-screen md:max-w-[1440px] sm:w-full mx-auto gap-4 sm:gap-6 md:gap-8 lg:gap-[52px] px-4 sm:px-6 md:px-8 lg:px-[80px] py-4 sm:py-6"
            role="region"
        >
            <header className="flex items-center justify-between">
                <Link
                    to="/dashboard/$program"
                    params={{ program: program ?? "weight-loss" }}
                    className="flex items-center gap-1.5 sm:gap-2 p-2 sm:p-2.5 w-fit bg-white rounded-lg border border-gray-100 transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary group"
                    aria-label="Return to dashboard"
                >
                    <ArrowLeft
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-foreground transition-transform group-hover:-translate-x-0.5"
                        aria-hidden="true"
                    />
                    <span className="text-xs sm:text-sm font-bold -mt-0.5">
                        Back
                    </span>
                </Link>
            </header>

            <main className="flex flex-col gap-2 sm:gap-3">
                <h2
                    className="text-2xl sm:text-3xl md:text-4xl font-bold"
                >
                    To-Dos
                </h2>
                <Tabs defaultValue="consultation" value={tab} onValueChange={handleTabChange}>
                    <div className="w-full flex overflow-x-auto pb-1 sm:pb-2">
                        <TabsList className="flex border-b no-scrollbar bg-transparent px-0 py-0 sm:py-1 whitespace-nowrap">
                            <TabsTrigger
                                value="consultation"
                                className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                            >
                                Consultations
                                {consultationsTodosEncountersQuery.data.totalCount > 0 && (
                                    <div className="bg-[#FFAB1F] text-white px-1.5 py-0.5 rounded-full text-xs flex items-center ml-1.5">
                                        {consultationsTodosEncountersQuery.data.totalCount}
                                    </div>
                                )}
                            </TabsTrigger>
                            <TabsTrigger
                                value="appointment"
                                className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                            >
                                Appointments
                                {appointmentsTodosEncountersQuery.data.totalCount > 0 && (
                                    <div className="bg-[#FFAB1F] text-white px-1.5 py-0.5 rounded-full text-xs flex items-center ml-1.5">
                                        {appointmentsTodosEncountersQuery.data.totalCount}
                                    </div>
                                )}
                            </TabsTrigger>
                            <TabsTrigger
                                value="refill"
                                className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                            >
                                Medications Refill
                                {refillsTodosEncountersQuery.data.totalCount > 0 && (
                                    <div className="bg-[#FFAB1F] text-white px-1.5 py-0.5 rounded-full text-xs flex items-center ml-1.5">
                                        {refillsTodosEncountersQuery.data.totalCount}
                                    </div>
                                )}
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    {tab && <TabsContent value={tab} className="mt-4 w-full sm:w-full">
                        {todosEncountersQuery.data.encounters.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] w-full gap-5 h-[calc(100vh-280px)] md:h-[calc(100vh-240px)]">
                                <div className="flex w-full md:w-[80%] flex-col gap-3 overflow-y-auto pr-2 hide-scrollbar">
                                    {todosEncountersQuery.data.encounters?.map((todo) => (
                                        <EncounterCard key={todo.id}
                                            item={todo}
                                            selectedEncounter={selectedEncounter}
                                            setSelectedEncounter={setSelectedEncounter}
                                            onOpenChange={setEncounterOpen}
                                            program={program}
                                        />
                                    ))}
                                </div>
                                <div className="w-full mt-6 lg:mt-0 overflow-y-auto pr-2 hide-scrollbar">
                                    <TodoDetails encounter={selectedEncounter} onOpenChange={setEncounterOpen} open={isEncounterOpen} />
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3 justify-center items-center py-10 md:py-16">
                                <h2 className="text-base md:text-lg font-medium text-center">You have no {toTitleCase(tab)}s Todo </h2>
                                <p className="text-xs md:text-sm text-gray-500 text-center">When you have things to do, they will appear here</p>
                            </div>
                        )}
                    </TabsContent>}
                </Tabs>
            </main>
        </section>
    );
}